import apiSlice from '../api/api.slice';
import { IFeedItem } from '../feed/feed.d';
import { IProjectGroupWithDetails } from '../projectGroup/projectGroup.d';

const FEED_ENDPOINTS = ['getFeed', 'getProfileFeed'] as const;

type LikeCacheApi = {
  dispatch: (action: unknown) => { undo: () => void };
  getState: () => unknown;
  queryFulfilled: Promise<unknown>;
};

/** Mirror a post like into every cached feed page, so returning to a feed
 * never shows a liked post as unliked. Feeds aren't invalidated: a refetch
 * would throw away the reader's place in them. */
async function syncFeedsWithLike(
  postId: string,
  liked: boolean,
  { dispatch, getState, queryFulfilled }: LikeCacheApi
) {
  const patches = FEED_ENDPOINTS.flatMap((endpoint) =>
    apiSlice.util
      .selectCachedArgsForQuery(getState() as never, endpoint as never)
      .map((args) =>
        dispatch(
          apiSlice.util.updateQueryData(
            endpoint as never,
            args as never,
            ((draft: { items?: IFeedItem[] }) => {
              const item = draft?.items?.find(
                (i) => i.type === 'post' && i._id === postId
              );
              if (!item || item.type !== 'post') return;
              if (!!item.hasLikedByUser === liked) return;
              item.hasLikedByUser = liked;
              item.likes += liked ? 1 : -1;
            }) as never
          )
        )
      )
  );
  try {
    await queryFulfilled;
  } catch {
    patches.forEach((p) => p.undo());
  }
}

const likeApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    likePost: builder.mutation<any, string>({
      query: (postId) => ({
        url: `/api/v1/like/create`,
        method: 'POST',
        body: {
          postId,
        },
      }),
      onQueryStarted: (postId, api) =>
        syncFeedsWithLike(postId, true, api as unknown as LikeCacheApi),
    }),
    unlikePost: builder.mutation<any, string>({
      query: (postId) => ({
        url: `/api/v1/like/delete?postId=${postId}`,
        method: 'DELETE',
      }),
      onQueryStarted: (postId, api) =>
        syncFeedsWithLike(postId, false, api as unknown as LikeCacheApi),
    }),
    // No invalidatesTags on purpose: a heart must not refetch the whole
    // thread and throw away the reader's place in it. The button holds
    // optimistic local state, the same way LikeButton does for posts.
    toggleCommentLike: builder.mutation<
      { liked: boolean; likesCount: number },
      string
    >({
      query: (commentId) => ({
        url: `/api/v1/like/comment/toggle`,
        method: 'POST',
        body: { commentId },
      }),
    }),
    likeProjectGroup: builder.mutation<any, string>({
      query: (projectGroupId) => ({
        url: `/api/v1/like/project-group/create`,
        method: 'POST',
        body: {
          projectGroupId,
        },
      }),
    }),
    unlikeProjectGroup: builder.mutation<any, string>({
      query: (projectGroupId) => ({
        url: `/api/v1/like/project-group/delete?projectGroupId=${projectGroupId}`,
        method: 'DELETE',
      }),
    }),
    // Toggle mutation with optimistic update
    toggleProjectGroupLike: builder.mutation<
      { liked: boolean },
      { projectGroupId: string; currentlyLiked: boolean }
    >({
      query: ({ projectGroupId }) => ({
        url: `/api/v1/like/project-group/toggle`,
        method: 'POST',
        body: {
          projectGroupId,
        },
      }),
      async onQueryStarted(
        { projectGroupId, currentlyLiked },
        { dispatch, queryFulfilled }
      ) {
        // Optimistically update the cache for getProjectGroupById
        const patchResult = dispatch(
          apiSlice.util.updateQueryData(
            'getProjectGroupById' as never,
            projectGroupId as never,
            // eslint-disable-next-line no-param-reassign
            (draft: IProjectGroupWithDetails) => {
              if (draft) {
                // eslint-disable-next-line no-param-reassign
                draft.hasLikedByUser = !currentlyLiked;
                // eslint-disable-next-line no-param-reassign
                draft.likes = currentlyLiked
                  ? draft.likes - 1
                  : draft.likes + 1;
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          // If the mutation fails, undo the optimistic update
          patchResult.undo();
        }
      },
    }),
  }),
});

apiSlice.enhanceEndpoints({
  addTagTypes: ['Like'],
  endpoints: {
    likePost: {
      invalidatesTags: ['Post'],
    },
    unlikePost: {
      invalidatesTags: ['Post'],
    },
    likeProjectGroup: {
      invalidatesTags: ['ProjectGroup'],
    },
    unlikeProjectGroup: {
      invalidatesTags: ['ProjectGroup'],
    },
    // Note: toggleProjectGroupLike doesn't invalidate tags since it uses optimistic updates
  },
});

export const {
  useLikePostMutation,
  useUnlikePostMutation,
  useLikeProjectGroupMutation,
  useUnlikeProjectGroupMutation,
  useToggleProjectGroupLikeMutation,
  useToggleCommentLikeMutation,
} = likeApiSlice;
